import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "Where can I get SAINT® TMS treatment?",
    answer:
      "SAINT® is available at a limited number of Verified SAINT® Providers nationwide. Neuro Wellness TMS Centers of America offers SAINT® on the FDA-cleared Magnus Neuromodulation System at 9734 W Sample Rd, Coral Springs, FL 33065. Because the protocol requires specialized equipment, fMRI targeting, and trained staff, it is not available at every TMS clinic. Contact the clinic directly to confirm availability and schedule a consultation.",
  },
  {
    question: "How do I verify that a provider actually offers SAINT®?",
    answer:
      "Ask the provider directly whether they use the Magnus Neuromodulation System with SAINT Technology and whether they perform fMRI-guided targeting. You can also check the Magnus Medical provider locator for verified sites. A clinic that offers 'accelerated TMS' without naming the Magnus system may be using an off-label schedule on a different device, which is not the same as SAINT®.",
  },
  {
    question: "Is SAINT® the same as accelerated TMS?",
    answer:
      "No. SAINT® is one specific accelerated TMS protocol: intermittent theta burst stimulation, 10 sessions per day for 5 days, with functional MRI targeting, delivered on the Magnus Neuromodulation System. 'Accelerated TMS' is a broader term for any schedule with more than one session per day. Only the Magnus system is FDA-cleared for the SAINT® protocol; other accelerated schedules are delivered off-label on cleared TMS devices.",
  },
  {
    question: "Can I travel to Coral Springs for SAINT® treatment?",
    answer:
      "Yes. Because the SAINT® course takes five consecutive days, patients from elsewhere in Florida and out of state travel to Neuro Wellness TMS Centers of America in Coral Springs for treatment. Plan to arrive the day before treatment starts, stay locally for the week, and expect full days at the clinic with roughly 50-minute breaks between sessions. A pre-treatment evaluation and MRI must be completed beforehand.",
  },
  {
    question: "What should I ask a SAINT® provider before starting?",
    answer:
      "Ask whether they use the Magnus Neuromodulation System with SAINT Technology, whether targeting is done with fMRI, how many patients they have treated with the protocol, what the total cost will be, whether they verify insurance benefits, and what the follow-up plan is if symptoms return. A reputable provider will answer these questions clearly and in writing.",
  },
  {
    question: "Does Neuro Wellness offer other treatments besides SAINT®?",
    answer:
      "Yes. Neuro Wellness TMS Centers of America also offers standard (traditional) TMS, SPRAVATO® (esketamine), Prism for PTSD, Proliv Rx at-home neuromodulation, IV ketamine infusions, ketamine-assisted psychotherapy, psychotherapy, and medication management. If SAINT® is not appropriate for your situation, the clinical team can discuss other options.",
  },
];

export const whereCanIGetSaint: LearnPageData = {
  slug: "where-can-i-get-saint-tms",
  title: "Where Can I Get SAINT® TMS? | Neuro Wellness",
  description:
    "SAINT® TMS is available at a limited number of verified providers. Learn how to find and evaluate a SAINT® provider, what to ask, and why patients travel to Coral Springs, FL.",
  canonical: "/learn/where-can-i-get-saint-treatment-for-depression",
  h1: "Where Can I Get SAINT® TMS?",
  breadcrumbLabel: "Where Can I Get SAINT® TMS?",
  crumbs: baseCrumbs("Where Can I Get SAINT® TMS?"),
  shortAnswer:
    "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is an FDA-cleared, fMRI-guided accelerated TMS protocol available at a limited number of Verified SAINT® Providers. Neuro Wellness TMS Centers of America offers SAINT® on the Magnus Neuromodulation System at 9734 W Sample Rd, Coral Springs, FL 33065. Because the protocol requires specialized equipment, brain imaging, and trained staff, it is not available at every TMS clinic. Patients from across Florida and other states travel to Coral Springs for the five-day treatment course.",
  keyTakeaways: [
    "SAINT® is delivered on the Magnus Neuromodulation System and requires fMRI-guided targeting — not every TMS clinic offers it.",
    "A 'Verified SAINT® Provider' uses the Magnus system with SAINT Technology; generic 'accelerated TMS' on another device is not the same.",
    "The full SAINT® course takes five consecutive days, which can make travel practical for out-of-area patients.",
    "Neuro Wellness TMS Centers of America is located in Coral Springs, Florida, serving Broward, Palm Beach, and Miami-Dade counties.",
    "A consultation and MRI are required before treatment begins; eligibility is determined by a psychiatrist.",
  ],

  sections: [
    {
      heading: "What Is SAINT® TMS?",
      body: (
        <>
          {P(
            "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is an accelerated form of transcranial magnetic stimulation (TMS) developed at Stanford University. It delivers intermittent theta burst stimulation — ten short sessions per day for five consecutive days — rather than the one-session-per-day schedule used in traditional TMS.",
          )}
          {P(
            "What sets SAINT® apart is its targeting. Before treatment, each patient undergoes a functional MRI (fMRI) scan. A proprietary algorithm analyzes the individual's brain connectivity and selects a personalized stimulation site in the left dorsolateral prefrontal cortex. Traditional TMS, by contrast, typically uses scalp measurements to estimate the target.",
          )}
          {P(
            "SAINT® is FDA-cleared (510(k) K220177, September 2022) for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. It is delivered on the Magnus Neuromodulation System with SAINT Technology, made by Magnus Medical, Inc.",
          )}
        </>
      ),
    },
    {
      heading: "Why SAINT® Is Not Available at Every TMS Clinic",
      body: (
        <>
          {P(
            "SAINT® requires specific technology and expertise that most TMS clinics do not have. The protocol depends on the Magnus Neuromodulation System, access to fMRI scanning, software that computes individualized targets from brain connectivity data, and clinical staff trained on the accelerated five-day schedule.",
          )}
          {P(
            "Traditional TMS devices — such as NeuroStar or BrainsWay systems — are installed in thousands of clinics nationwide. The Magnus system with SAINT Technology is available at a smaller number of Verified SAINT® Providers. A clinic may offer 'accelerated TMS' using a different device on an off-label schedule, but that is not the same as the FDA-cleared SAINT® protocol.",
          )}
          {P(
            "If a provider cannot confirm they use the Magnus Neuromodulation System with fMRI-guided targeting, the treatment they offer is not SAINT®.",
          )}
        </>
      ),
    },
    {
      heading: "How to Verify a SAINT® Provider",
      body: (
        <>
          {P(
            "Before committing to treatment, confirm that the provider actually offers SAINT® on the Magnus system. The following steps help you verify this:",
          )}
          <ul className="mt-4 space-y-2">
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Ask directly whether the clinic uses the Magnus Neuromodulation
              System with SAINT Technology.
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Confirm that stimulation targeting is based on a functional MRI
              scan, not scalp measurements alone.
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Check the Magnus Medical provider locator for verified sites.
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Ask how many patients the clinic has treated with the SAINT®
              protocol specifically.
            </li>
          </ul>
          {P(
            "A reputable provider will answer these questions clearly and without hesitation. If a clinic is vague about the device or the targeting method, consider it a reason to ask more questions before proceeding.",
          )}
        </>
      ),
    },
    {
      heading: "The Difference Between SAINT® and Generic Accelerated TMS",
      body: (
        <>
          {P(
            "'Accelerated TMS' is a broad term for any TMS schedule that delivers more than one session per day. Several clinics offer accelerated schedules on FDA-cleared devices like NeuroStar or BrainsWay. These are off-label uses — the FDA has not reviewed those specific schedules for safety or effectiveness, and insurance typically does not cover them.",
          )}
          {P(
            "SAINT® is the only accelerated TMS protocol with its own FDA clearance. It is defined by three elements: the Magnus Neuromodulation System, fMRI-guided personalized targeting, and a specific intermittent theta burst schedule of ten sessions per day for five days. If any of these elements is missing, the treatment is not SAINT®.",
          )}
          {P(
            <>
              This distinction matters for patients. Learn more about{" "}
              <a
                href="/learn/accelerated-tms-theta-burst-stimulation"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                accelerated TMS and theta burst stimulation
              </a>{" "}
              or read our{" "}
              <a
                href="/learn/what-is-saint-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                detailed overview of what SAINT® is
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "What a SAINT® Consultation Typically Involves",
      body: (
        <>
          {P(
            "A SAINT® consultation is a clinical evaluation, not a sales call. During the consultation, a psychiatrist or qualified clinician reviews your diagnosis, medication history, and overall health. They screen for conditions that may affect eligibility — such as bipolar disorder, psychosis, seizure history, or metal implants — and discuss whether SAINT® is appropriate for your situation.",
          )}
          {P(
            "If you appear to be a candidate, the next steps include scheduling an fMRI scan and planning the five-day treatment week. The consultation is also the time to discuss cost, insurance verification, and logistics such as travel and lodging if you are coming from out of town.",
          )}
          {P(
            <>
              You can{" "}
              <a
                href="/am-i-a-candidate"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                request a consultation online
              </a>{" "}
              or call 954-827-9793 to speak with the Neuro Wellness care team.
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Why Patients Sometimes Travel for SAINT® Treatment",
      body: (
        <>
          {P(
            "Because SAINT® is available at a limited number of sites, many patients travel to reach a verified provider. The five-day schedule can actually make travel more practical than a traditional six-week course: instead of commuting to a clinic 36 times over two months, a patient travels once, stays locally for one week, and completes the entire course.",
          )}
          {P(
            "Patients from elsewhere in Florida — including Miami, Fort Lauderdale, Boca Raton, and West Palm Beach — as well as out-of-state patients travel to Neuro Wellness TMS Centers of America in Coral Springs for SAINT®. Plan to arrive the day before treatment, stay for five treatment days, and arrange a follow-up plan with your home clinicians before you leave.",
          )}
          {P(
            <>
              See our{" "}
              <a
                href="/traveling-for-treatment"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                traveling for treatment guide
              </a>{" "}
              for planning details.
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Questions to Ask a SAINT® Provider",
      body: (
        <>
          {P(
            "When evaluating any SAINT® provider, consider asking the following questions:",
          )}
          <ul className="mt-4 space-y-2">
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Do you use the Magnus Neuromodulation System with SAINT
              Technology?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Is the stimulation target chosen from my own functional MRI?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              How many patients have you treated with the SAINT® protocol?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              What is the total cost, and do you verify insurance benefits?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              What is the follow-up plan if symptoms return after treatment?
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Who supervises the medical care, and what are their credentials?
            </li>
          </ul>
        </>
      ),
    },
    {
      heading: "Neuro Wellness TMS Centers of America",
      body: (
        <>
          {P(
            "Neuro Wellness TMS Centers of America is an outpatient mental health treatment center located at 9734 W Sample Rd, 2nd Floor, Suite B, Coral Springs, FL 33065. The clinic offers SAINT® on the Magnus Neuromodulation System, along with traditional TMS, SPRAVATO® (esketamine), Prism for PTSD, Proliv Rx at-home neuromodulation, IV ketamine infusions, psychotherapy, and medication management.",
          )}
          {P(
            "The clinic serves patients from Broward, Palm Beach, and Miami-Dade counties, and many patients travel from elsewhere in Florida or from other states for the five-day SAINT® course. To learn whether SAINT® may be appropriate for you, request a consultation with the clinical team.",
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Magnus Medical. SAINT® Technology and Verified Provider information.",
      url: "https://magnusmedical.com",
    },
    {
      citation:
        "Stanford Medicine. Stanford Brain Stimulation Lab — SAINT/SNT research.",
      url: "https://med.stanford.edu/brainstimulation",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/where-can-i-get-saint-tms#webpage`,
        name: "Where Can I Get SAINT® TMS?",
        url: `${SITE_URL}/learn/where-can-i-get-saint-tms`,
        description:
          "SAINT® TMS is available at a limited number of verified providers. Learn how to find and evaluate a SAINT® provider, what to ask, and why patients travel to Coral Springs, FL.",
        about: [
          {
            "@id": `${SITE_URL}/learn/where-can-i-get-saint-tms#therapy`,
          },
          { "@id": `${SITE_URL}/#condition-mdd` },
          { "@id": `${SITE_URL}/#condition-trd` },
        ],
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        "@id": `${SITE_URL}/learn/where-can-i-get-saint-tms#therapy`,
        name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
        alternateName: ["SAINT TMS", "Stanford Neuromodulation Therapy (SNT)"],
        description:
          "Accelerated, fMRI-guided intermittent theta burst stimulation delivered on the Magnus Neuromodulation System with SAINT Technology (FDA 510(k) K220177): 10 sessions per day for 5 consecutive days.",
        relevantSpecialty: "Psychiatric",
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/#condition-mdd`,
        name: "Major depressive disorder",
        code: { "@type": "MedicalCode", code: "D003865", codingSystem: "MeSH" },
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/#condition-trd`,
        name: "Treatment-resistant depression",
      },
      faqJsonld(faqs, `${SITE_URL}/learn/where-can-i-get-saint-tms#faq`),
      breadcrumbJsonld(baseCrumbs("Where Can I Get SAINT® TMS?")),
    ],
  },
  relatedLinks: [
    {
      title: "SAINT® Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol offered at our clinic",
    },
    {
      title: "What Is SAINT® TMS?",
      href: "/learn/what-is-saint-tms",
      description: "FDA clearance, protocol, and how SAINT differs from TMS",
    },
    {
      title: "Find a Verified SAINT® Provider",
      href: "/find-a-verified-saint-provider",
      description: "How to verify a SAINT® provider and why it matters",
    },
    {
      title: "SAINT® TMS Cost",
      href: "/learn/saint-tms-cost",
      description: "What SAINT® costs and how to plan for payment",
    },
    {
      title: "Does Insurance Cover SAINT® TMS?",
      href: "/learn/does-insurance-cover-saint-tms",
      description: "Coverage, prior authorization, and benefits verification",
    },
    {
      title: "Traveling for Treatment",
      href: "/traveling-for-treatment",
      description: "Planning your visit to Coral Springs for SAINT®",
    },
  ],
};
